import i18n, { type TFunction } from 'i18next';
import HttpBackend, {
  type HttpBackendOptions,
  type RequestCallback,
} from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import { api } from '@/lib/api-client';

const DEFAULT_LANGUAGE = 'nl-NL';

/**
 * Swap client specific translations.
 *
 * @remarks
 * The input object contains key value pairs of translations for the
 * specific language. We loop through each key and check if there is
 * a client specific key. For eg: If the translation key is "dom_x" and
 * clientId is 50, we check if there is a translation "dom_x_50".
 * If there is client specific translation, we swap the value of "dom_x"
 * with the value of "dom_x_50".
 *
 * @param translations - Translations object containing key value pairs.
 * @param clientId - Client id of the user.
 * @returns - Object containing swapped client specific translations.
 */
export function swapClientTranslations(
  translations: Record<string, string>,
  clientId: number,
): Record<string, string> {
  if (!clientId) return translations;

  const trailingString = `_${clientId}`;

  const swappedTranslations: Record<string, string> = {};

  for (const [key, value] of Object.entries(translations)) {
    const swappableKey = key + trailingString;
    if (translations[swappableKey]) {
      swappedTranslations[key] = translations[swappableKey];
    } else {
      swappedTranslations[key] = value;
    }
  }

  return swappedTranslations;
}

const backendOptions: HttpBackendOptions = {
  loadPath: '{{lng}}|{{ns}}',
  request: (
    _options: HttpBackendOptions,
    url: string,
    _payload: object,
    callback: RequestCallback,
  ) => {
    try {
      const [lngCode] = url.split('|');

      const [language, country] = lngCode.split('-');

      api
        .get<Record<string, string>>('/translations', {
          params: { language, country },
        })
        .then((response) => {
          callback(null, {
            data: swapClientTranslations(response.data, 353),
            status: response.status || 200,
          });
        })
        .catch((error) => {
          console.error(error, 'Failed to fetch translations from the API');
          callback(error, {
            data: {},
            status: error.response?.status || 500,
          });
        });
    } catch (e) {
      console.error(e, 'Failed to set translations');
      callback(null, {
        data: {},
        status: 500,
      });
    }
  },
};

/**
 * Initializes i18next translations support.
 */
export const initI18next = ():
  | Promise<TFunction<'translation', undefined>>
  | Promise<void> => {
  if (!i18n.isInitialized) {
    return i18n
      .use(HttpBackend)
      .use(initReactI18next)
      .init({
        load: 'currentOnly', // Only load set language.
        lng: DEFAULT_LANGUAGE,
        fallbackLng: undefined, // Fallbacks are set in the API.
        interpolation: { escapeValue: false },
        retryTimeout: 500, // Slightly higher than default to prevent API overload.
        maxRetries: 3,
        react: {
          useSuspense: true,
        },
        backend: backendOptions,
      });
  } else {
    console.warn('i18next is already initialized');
    return Promise.resolve();
  }
};
